import { Quote } from "../../shared/types/quotes";
/**
 * Formats the list of products and packages for display.
 *
 * @param {Array<{ name: string }>} products - List of products.
 * @param {Array<{ name: string, quoteProducts: [ { name: string } ] }>} packages - List of packages.
 * @returns {string[]} - Formatted list of product and package names.
 */
const formatProductsAndPackages = (
    products: Array<{ name: string }>,
    packages: Array<{ name: string, quoteProducts: [ { name: string } ] }>,
  ) => {
    return [
      ...products.map((p) => p?.name.trim()), 
      ...packages.map((pack) => pack?.name.trim())
      // ...packages.map((pack) => pack?.quoteProducts?.map((p) => p.name.trim()))
    ].flat();
};

/**
 * Creates a quote row with provided data.
 *
 * @param {string | number} quoteId - The quote's ID.
 * @param {string} insuredName - The insured name.
 * @param {Date | null | string} inceptionDate - The inception date.
 * @param {Date | null | string} lastModified - The last modified date.
 * @param {any} quotes - Quotes data.
 * @param {any} status - Quote status.
 * @returns {QuoteData} - A formatted quote data object.
 */
export const createQuoteRow = (
    quoteId: string | number,
    insuredName: string,
    inceptionDate: Date | null | string,
    lastModified: Date | null | string,
    quotes: any,
    // products: Array<{ name: string }>,
    // packages: Array<{ name: string, quoteProducts: [ { name: string } ] }>,
    status: any,
    clientHash: string | null,
    ) => {
    // let productsShown = formatProductsAndPackages(products, packages);
    let productsShown = "";
    let statusName = status && status != "loading" && ("name" in status) ? status.name : "Status not found";

    return {
        id: quoteId ?? "Null",
        insuredName: insuredName ? insuredName : "Name not found",
        inceptionDate: inceptionDate,
        modifiedDate: lastModified,
        quotes: quotes,
        // products: productsShown,
        status: statusName,
        clientHash: clientHash,
    };
};

/**
 * Represents the data structure for a quote.
 */
export interface QuoteData {
    id: number | string;
    insuredName: string;
    inceptionDate: Date | string | null;
    modifiedDate: Date | string | null;
    quotes: Quote[];
    // products: any[];
    status: string;
    clientHash: string | null;
}

/**
 * Compares two values for descending sorting.
 *
 * @param {T} a - First value.
 * @param {T} b - Second value.
 * @param {keyof T} orderBy - The key to sort by.
 * @returns {number} - Result of the comparison.
 */
export function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    const aOrderBy = typeof a[orderBy] === "string" ? `${a[orderBy]}`.toLowerCase() : a[orderBy];
    const bOrderBy = typeof b[orderBy] === "string" ? `${b[orderBy]}`.toLowerCase() : b[orderBy];
    // if (b[orderBy] < a[orderBy]) {
    if (bOrderBy < aOrderBy) {
      return -1;
    }
    // if (b[orderBy] > a[orderBy]) {
    if (bOrderBy > aOrderBy) {
      return 1;
    }
    return 0;
}

/**
 * Represents the sorting order, either 'asc' or 'desc'.
 */
export type Order = 'asc' | 'desc';

/**
 * Returns a comparator function for sorting data.
 *
 * @param {Order} order - Sorting order ('asc' or 'desc').
 * @param {Key} orderBy - Key to sort by.
 * @returns {function} - A comparator function.
 */
export function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
  ): (a: any, b: any) => number {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
}

/**
 * Stably sorts an array using a comparator function.
 *
 * @param {T[]} array - The array to sort.
 * @param {(a: T, b: T) => number} comparator - Comparator function.
 * @returns {T[]} - The sorted array.
 */
export function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}
  
/**
 * Represents the structure of a table header cell.
 */
export interface HeadCell {
    id: keyof QuoteData;
    label: string;
    numeric: boolean;
    sortable?: boolean | null;
    component?: any;
    colSpan?: number;
    width?: number | string;
}

/**
 * An array of table header cells.
 */
export const headCells: HeadCell[] = [
    { id: 'insuredName', numeric: false, label: 'Insured name' },
    { id: 'inceptionDate', numeric: false, label: 'Inception date' },
    { id: 'modifiedDate', numeric: false, label: 'Last modified' },
    { id: 'quotes', numeric: false, label: 'Product(s)', sortable: false, colSpan: 2,},
    // { id: 'products', numeric: false, label: 'Product(s)', sortable: false },
    { id: 'status', numeric: false, label: 'Status', sortable: false },
    { id: 'id', numeric: false, label: 'Manage', sortable: false },
];